import type { ErrorEvent } from '@sentry/nextjs';

/**
 * Sentry 이벤트의 `contexts.axios` 블록을 만든다.
 *
 * 동기: ZEP-394 이후 axios 에러 메시지에 `[METHOD URL STATUS]`가 박혔지만,
 * 디버깅에는 요청 payload / 응답 body가 필수다. 그게 없으니 500 케이스
 * (예: QUIZ-APP-70V "Internal Server Error")는 Sentry 화면만 보고는 무엇이
 * 잘못 갔는지 판별 불가했다. 이 모듈이 그 갭을 메운다.
 *
 * 입력 후보 (코드베이스 내 실재 형태):
 *  - 그대로의 `AxiosError` (admin 등): config/response 보유
 *  - `annotateAxiosError`로 가공된 AxiosError (play-app/web-app): + httpStatus/status
 *  - `ZepQuizApiError` (quiz-app): 요청 config(비열거) + `details = response.data` 보유
 *  - `ZepApiError` (play-app/web-app): backend status<0 응답을 wrapping
 */

// 키 이름 일부만 맞아도 가린다(userEmail·verifiedPhoneNumber·sessionToken 등).
// 키 목록은 zep-quiz-spring-server 요청·응답 DTO 필드명 기준.
const SENSITIVE_KEY_PART = [
  'password|passwd|secret|authorization|cookie|credential|api[-_]?key',
  'token|sessionkey|linkingsession',
  'email|phone|address|zip|clientip|birth|resident',
  'logincode|usercode|verificationcode|authcode|classcode',
  'pincode|pinnumber',
  'paymentkey|billingkey|customerkey|card[-_]?(?:number|no|password|expiration)',
  'cvc|cvv|virtualaccount|accountnumber',
  'institution|organizationname|companyname|filename',
].join('|');
// 짧아서 다른 단어에 섞이는 키(shipping·classname·screenshotPath)는 키 전체가 같을 때만.
// `input` 은 FastAPI 422 응답이 입력 원문을 돌려주는 자리.
const SENSITIVE_KEY_EXACT = 'pin|card|ssn|otp|input|rejectedvalue';

const SENSITIVE_KEY_RE = new RegExp(
  `${SENSITIVE_KEY_PART}|^(?:${SENSITIVE_KEY_EXACT})$`,
  'i',
);

// 내용 대신 길이·개수만 남겨 "너무 길다/비었다" 류 400 은 구분할 수 있게 한다.
// 이름(반에서는 보통 실명)은 요청·응답 모두, 학생이 쓴 자유 입력은 요청에만 적용한다.
// 응답의 `message` 는 서버 에러 문구라 남겨야 한다. 자유 입력은 끝이 일치할 때만
// 가려 contentId·messageCount 같은 식별자·숫자는 남긴다.
const NAME_KEY_RE =
  /displayname|nickname|username|studentname|ownername|membername|^name$/i;
const FREE_TEXT_KEY_RE =
  /(?:message|messages|messagehistory|content|text|prompt|comment|answer|memo|feedback|title|description|details|question|query|purpose)$/i;
// 맨 `code` 는 SMS 인증번호·OAuth 코드라 요청에서만 가린다(응답 `code` 는 결제사 에러 코드).
const REQUEST_ONLY_SECRET_KEY_RE = /^code$/i;

// 위 규칙에 걸려도 남기는 키 — 개수·길이(maxTokens·tokenCount)와 `context`(끝이 text).
const SAFE_KEY_RE = /^context$|(?:count|length|size|maxtokens)$/i;

const RESPONSE_KEY_RE = new RegExp(
  `${SENSITIVE_KEY_RE.source}|${NAME_KEY_RE.source}`,
  'i',
);
const REQUEST_KEY_RE = new RegExp(
  `${RESPONSE_KEY_RE.source}|${FREE_TEXT_KEY_RE.source}|${REQUEST_ONLY_SECRET_KEY_RE.source}`,
  'i',
);

// 객체 키 순회로는 못 잡는 form-urlencoded(`password=xxx&token=yyy`) 평문 케이스를 위해
// 문자열 원문에도 마스킹을 적용한다. JSON 파싱이 실패한 string body / 외부에서 들어온
// query-string 형태에 모두 통한다. (lookbehind 는 구형 Safari 에서 문법 에러라 쓰지 않는다)
// url 쿼리·form 문자열에는 키 구분이 없어 이름·인증 코드(`?code=`)도 같이 가린다.
const SENSITIVE_FORM_RE = new RegExp(
  `(^|[^\\w-])([\\w-]*(?:${SENSITIVE_KEY_PART}|displayname|nickname|username)[\\w-]*|${SENSITIVE_KEY_EXACT}|code|name)=[^&\\s]*`,
  'gi',
);

// 키 이름과 상관없이 값에 박힌 이메일(에러 메시지·loginId 등)도 가린다.
const EMAIL_VALUE_RE = /[\w.%+-]+@[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}/gi;

function scrubSensitiveString(value: string): string {
  return value
    .replace(SENSITIVE_FORM_RE, '$1$2=[Scrubbed]')
    .replace(EMAIL_VALUE_RE, '[Email]');
}

/** 컨텍스트 항목당 사이즈 캡 — Sentry 이벤트 폭발 방지 */
const MAX_FIELD_BYTES = 4_000;

/** 한 번 직렬화에서 정규식을 돌릴 문자열 총량 — 키가 수백 개인 응답에서 beforeSend 가 느려지지 않게 */
const MAX_SCRUB_CHARS = 16_000;

/** 객체 키 한도 */
const MAX_OBJECT_KEYS = 100;

/** 객체 깊이 한도 — 순환 참조/거대 트리 방지 */
const MAX_DEPTH = 4;

/** 배열 항목 한도 */
const MAX_ARRAY_ITEMS = 50;

function scrubbedLabel(key: string, value: unknown): string {
  const keepLength =
    !SENSITIVE_KEY_RE.test(key) &&
    !REQUEST_ONLY_SECRET_KEY_RE.test(key) &&
    (NAME_KEY_RE.test(key) || FREE_TEXT_KEY_RE.test(key));
  if (keepLength && typeof value === 'string') {
    return `[Scrubbed:len=${value.length}]`;
  }
  if (keepLength && Array.isArray(value)) {
    return `[Scrubbed:items=${value.length}]`;
  }
  return '[Scrubbed]';
}

export function scrubSensitive(
  value: unknown,
  depth = 0,
  keyRe: RegExp = RESPONSE_KEY_RE,
  budget = { chars: MAX_SCRUB_CHARS },
): unknown {
  if (value == null) return value;
  if (depth > MAX_DEPTH) return '[Truncated]';
  if (typeof value === 'string') {
    if (budget.chars <= 0) return '[Truncated]';
    // 수백 KB 본문에 정규식을 돌리지 않게 먼저 자른다.
    const limit = Math.min(MAX_FIELD_BYTES, budget.chars);
    budget.chars -= Math.min(value.length, limit);
    return value.length > limit
      ? `${scrubSensitiveString(value.slice(0, limit))}…(truncated)`
      : scrubSensitiveString(value);
  }
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map(item => scrubSensitive(item, depth + 1, keyRe, budget));
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .slice(0, MAX_OBJECT_KEYS)
      .map(([key, v]) =>
        // 불리언(emailConsent 등)·null 은 가릴 정보가 없어 남긴다.
        keyRe.test(key) &&
        !SAFE_KEY_RE.test(key) &&
        typeof v !== 'boolean' &&
        v != null
          ? [key, scrubbedLabel(key, v)]
          : [key, scrubSensitive(v, depth + 1, keyRe, budget)],
      ),
  );
}

export function safeJsonStringify(
  value: unknown,
  keyRe: RegExp = RESPONSE_KEY_RE,
): string | undefined {
  if (value === undefined) return undefined;
  try {
    const scrubbed = scrubSensitive(value, 0, keyRe);
    const json = JSON.stringify(scrubbed);
    if (json == null) return undefined;
    return json.length > MAX_FIELD_BYTES
      ? `${json.slice(0, MAX_FIELD_BYTES)}…(truncated)`
      : json;
  } catch {
    return '[Unserializable]';
  }
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function pickTraceId(
  headers: Record<string, unknown> | undefined,
): string | undefined {
  if (!headers) return undefined;
  const candidate =
    headers['x-zep-trace-id'] ??
    headers['X-ZEP-Trace-Id'] ??
    headers['x-request-id'] ??
    headers['X-Request-Id'];
  return typeof candidate === 'string' ? candidate : undefined;
}

interface AxiosErrorLike {
  config?: {
    method?: string;
    url?: string;
    baseURL?: string;
    params?: unknown;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
  response?: {
    status?: number;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
}

interface ZepQuizApiErrorLike {
  details?: unknown;
}

export interface AxiosContext {
  method?: string;
  url?: string;
  baseURL?: string;
  params?: string;
  requestBody?: string;
  responseStatus?: number;
  responseBody?: string;
  requestId?: string;
  // Sentry의 Context 타입(`Record<string, unknown>`)에 호환되도록 index signature를 둔다.
  [key: string]: unknown;
}

/**
 * 에러 객체에서 axios 컨텍스트를 추출한다.
 * 하나라도 의미 있는 필드를 채우지 못하면 null 반환.
 */
export function buildAxiosContext(error: unknown): AxiosContext | null {
  if (error == null || typeof error !== 'object') return null;

  const axiosLike = error as AxiosErrorLike;
  const zepQuiz = error as ZepQuizApiErrorLike;
  const ctx: AxiosContext = {};

  if (axiosLike.config) {
    // buildAxiosContext는 axios가 아닌 임의 originalException에도 호출되므로
    // method가 string이 아닌 경우(숫자/객체) toUpperCase가 throw → beforeSend 자체가
    // 실패해 이벤트가 드랍될 수 있다. 타입 가드 필수.
    if (typeof axiosLike.config.method === 'string') {
      ctx.method = axiosLike.config.method.toUpperCase();
    }
    if (typeof axiosLike.config.url === 'string') {
      ctx.url = scrubSensitiveString(axiosLike.config.url);
    }
    if (axiosLike.config.baseURL) ctx.baseURL = axiosLike.config.baseURL;
    if (axiosLike.config.params != null) {
      ctx.params = safeJsonStringify(axiosLike.config.params, REQUEST_KEY_RE);
    }
    if (axiosLike.config.data != null) {
      const data =
        typeof axiosLike.config.data === 'string'
          ? safeParseJson(axiosLike.config.data)
          : axiosLike.config.data;
      ctx.requestBody = safeJsonStringify(data, REQUEST_KEY_RE);
    }
  }

  if (axiosLike.response) {
    if (typeof axiosLike.response.status === 'number') {
      ctx.responseStatus = axiosLike.response.status;
    }
    if (axiosLike.response.data != null) {
      ctx.responseBody = safeJsonStringify(axiosLike.response.data);
    }
  }

  // X-ZEP-Trace-Id는 ClickHouse otel_traces의 SpanAttributes['http.x_trace_id']와 매칭되는
  // correlation 키. Sentry → ClickHouse 점프의 핵심이라 응답 헤더 우선, 없으면(네트워크
  // 에러로 응답 자체가 없는 경우) request 헤더에서 fallback으로 잡는다.
  const reqId =
    pickTraceId(axiosLike.response?.headers) ??
    pickTraceId(axiosLike.config?.headers);
  if (reqId) ctx.requestId = reqId;

  // ZepQuizApiError는 response 대신 details(=response.data)를 들고 있다.
  if (ctx.responseBody == null && zepQuiz.details != null) {
    ctx.responseBody = safeJsonStringify(zepQuiz.details);
  }

  return Object.keys(ctx).length > 0 ? ctx : null;
}

/** event.contexts.axios에 합쳐 넣는다. 이미 다른 contexts가 있어도 보존. */
export function attachAxiosContext(event: ErrorEvent, error: unknown): void {
  // beforeSend 경로의 어떤 예외도 이벤트 자체를 드랍시키면 안 된다(컨텍스트 부착은
  // best-effort, 실패해도 원래 에러는 Sentry로 가야 한다).
  try {
    const ctx = buildAxiosContext(error);
    if (!ctx) return;
    event.contexts = { ...event.contexts, axios: ctx };
  } catch {
    // swallow: context 부착 실패가 이벤트 손실로 이어지면 안 됨
  }
}
